import React, { useRef, useState, useEffect } from "react";
import * as faceapi from 'face-api.js';
import api from "../config/api";

export default function FaceCheck({ open, onClose, onSuccess }) {
  const videoRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [registered, setRegistered] = useState(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        console.log('✅ Camera started for verification');
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Gagal mengakses kamera");
    }
  };

  const loadModels = async () => {
    try {
      const USE_CDN = true;
      const MODEL_URL = USE_CDN 
        ? 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model'
        : window.location.origin + '/models';
      
      console.log('🔄 Loading face-api models for verification...');
      
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
      
      setModelsLoaded(true);
      console.log('✅ Face-api models loaded for verification');
    } catch (err) {
      console.error('❌ Error loading models:', err);
      setModelsLoaded(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await api.get('/api/face');
      setRegistered(res.data.registered);
    } catch (err) {
      setRegistered(false);
    }
  };

  const captureImage = () => {
    const canvas = document.createElement("canvas");
    const scale = 0.5;
    canvas.width = videoRef.current.videoWidth * scale;
    canvas.height = videoRef.current.videoHeight * scale;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.7);
  };

  const handleVerify = async () => {
    if (!modelsLoaded) {
      alert('Model wajah belum dimuat, tunggu sebentar...');
      return;
    }

    setCapturing(true);
    
    try {
      const imageData = captureImage();

      // Detect faces in the captured image
      const img = new Image();
      img.src = imageData;
      await new Promise(resolve => img.onload = resolve);

      console.log('🔍 Detecting faces for verification...');
      
      const options = new faceapi.TinyFaceDetectorOptions({
        inputSize: 416,
        scoreThreshold: 0.3
      });
      
      const detections = await faceapi.detectAllFaces(img, options);
      
      console.log('📊 Faces detected:', detections.length);

      if (detections.length === 0) {
        alert('❌ Tidak ada wajah terdeteksi!\n\nPastikan:\n• Wajah menghadap kamera\n• Pencahayaan cukup\n• Tidak memakai masker');
        setCapturing(false);
        return;
      }

      if (detections.length > 1) {
        alert('⚠️ Terlalu banyak wajah terdeteksi (' + detections.length + ').\nPastikan hanya wajah Anda yang terlihat.');
        setCapturing(false);
        return;
      }

      console.log('✅ Single face detected, confidence:', detections[0].score);

      // Kirim ke backend untuk verifikasi dengan database
      const res = await api.post("/api/face/verify", { image: imageData });
      if (res.data.success) {
        alert("✅ Wajah terverifikasi");
        stopCamera();
        onSuccess();
        onClose();
      } else {
        alert("❌ Verifikasi wajah gagal - wajah tidak cocok dengan data yang terdaftar");
      }
    } catch (err) {
      console.error("Error verifikasi wajah:", err);
      alert("Gagal verifikasi wajah: " + (err.response?.data?.message || err.message));
    } finally {
      setCapturing(false);
    }
  };

  useEffect(() => {
    if (open) {
      const init = async () => {
        await fetchStatus();
        await loadModels();
        await startCamera();
      };
      init();
    } else {
      stopCamera();
    }

    // Cleanup saat component unmount
    return () => {
      stopCamera();
    };
  }, [open]);

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.7)", display: open ? "flex" : "none",
      justifyContent: "center", alignItems: "center", zIndex: 9999
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, padding: 20, width: "90%", maxWidth: 400
      }}>
        <h3 style={{ marginBottom: 12 }}>Verifikasi Wajah</h3>
        
        <div style={{ position: 'relative' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", borderRadius: 8, marginBottom: 12, background: '#000' }}
          />
          
          {/* Overlay guide */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '70%',
            border: '2px dashed rgba(255, 255, 255, 0.6)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }} />
        </div>

        {!modelsLoaded && (
          <div style={{ color: '#f59e0b', marginBottom: 12, fontSize: 13, textAlign: 'center' }}>
            ⏳ Memuat model deteksi wajah...
          </div>
        )}
        
        {registered === false && (
          <div style={{ color: 'red', marginBottom: 12, fontSize: 14 }}>
            ❌ Anda belum mendaftarkan wajah. Silakan registrasi wajah terlebih dahulu di halaman Profile.
          </div>
        )}
        
        {modelsLoaded && registered && (
          <div style={{ background: '#e0f2fe', color: '#0369a1', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
            💡 Posisikan wajah di dalam lingkaran dan pastikan pencahayaan cukup
          </div>
        )}
        
        <button
          onClick={handleVerify}
          disabled={capturing || registered === false || !modelsLoaded}
          style={{
            background: (registered === false || !modelsLoaded) ? '#aaa' : '#28a745', 
            color: "#fff", 
            padding: "10px 20px",
            border: "none", 
            borderRadius: 6, 
            cursor: (registered === false || !modelsLoaded) ? 'not-allowed' : 'pointer'
          }}
        >
          {capturing ? "Memverifikasi..." : !modelsLoaded ? "Memuat..." : "Verifikasi Sekarang"}
        </button>
        <button
          onClick={() => {
            stopCamera();
            onClose();
          }}
          style={{
            marginLeft: 10, background: "#ccc", padding: "10px 20px",
            border: "none", borderRadius: 6, cursor: "pointer"
          }}
        >
          Batal
        </button>
      </div>
    </div>
  );
}
